import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import PendingChangesModal from './pendingChangesModal';
import { useApproveTargetSubmission, useRejectTargetSubmission } from '../../services/mutations';
import { useReportDetailStore } from '.';

const LinkToPendingChanges = styled.p`
  display: inline-flex;
  flex-direction: row;
  justify-content: flex-start;
  aling-items: center;
  gap: 5px;
  cursor: pointer;
  margin-bottom: 0;
  color: ${({ theme }) => theme.colors.negative};

  span {
    text-decoration: underline;
  }

  &:hover {
    span {
      text-decoration: none;
    }
  }
`;

function ApprovalsInfo({
  data,
  reasonsData,
  loading,
  refetching,
}: {
  data: any;
  reasonsData: any[];
  loading: boolean;
  refetching: boolean;
}) {
  const [isOpen, setOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const approveSubmission = useApproveTargetSubmission();
  const rejectSubmission = useRejectTargetSubmission();

  const { resultToast, userPermissions } = useReportDetailStore();

  const userCanApprove = userPermissions.canApproveTarget || userPermissions.canApproveDivisionalTarget;

  const handleApprove = (submissionId: number) => {
    approveSubmission.mutate(
      { submissionId },
      {
        onSuccess: () => {
          resultToast.current?.show({
            severity: 'success',
            summary: 'Submission approval',
            detail: 'Submission has been successfully approved.',
            life: 3000,
          });
        },
        onError: () => {
          resultToast.current?.show({
            severity: 'error',
            summary: 'Submission approval',
            detail: 'An error has occurred while approving submission.',
            life: 3000,
          });
        },
        onSettled: () => {
          //don't re-open the Pending changes for approval modal window
          setOpen(false);
        },
      },
    );
  };

  const handleReject = (submissionId: number) => {
    rejectSubmission.mutate(
      { submissionId },
      {
        onSuccess: () => {
          resultToast.current?.show({
            severity: 'success',
            summary: 'Submission rejection',
            detail: 'Submission has been successfully rejected.',
            life: 3000,
          });
        },
        onError: () => {
          resultToast.current?.show({
            severity: 'error',
            summary: 'Submission rejection',
            detail: 'An error has occurred while rejecting submission.',
            life: 3000,
          });
        },
        onSettled: () => {
          //don't re-open the Pending changes for approval modal window
          setOpen(false);
        },
      },
    );
  };

  useEffect(() => {
    if (userCanApprove && searchParams.get('action') === 'approvement') {
      // open the Pending changes for approval modal
      setOpen(true);
      setSearchParams(undefined);
    }
  }, []);

  useEffect(() => {
    if (loading) {
      return;
    }

    if (!data || data.length === 0) {
      setOpen(false);
    }
  }, [data, loading]);

  if (loading || !data || data.length === 0) {
    return null;
  }

  return (
    <>
      <PendingChangesModal
        isOpen={isOpen}
        loading={refetching || approveSubmission.isLoading || rejectSubmission.isLoading}
        canApproveTarget={userCanApprove}
        data={data}
        onCancel={() => setOpen(false)}
        onApprove={handleApprove}
        onReject={handleReject}
        reasonsData={reasonsData}
      />
      <LinkToPendingChanges onClick={() => setOpen(true)}>
        <i className="pi pi-exclamation-circle" />
        <span>{userCanApprove ? 'Pending changes for approval' : 'Your changes waiting for approval'}</span>
      </LinkToPendingChanges>
    </>
  );
}

export default ApprovalsInfo;
